import cn from 'classnames';
import React, { useState } from 'react';
import ImageComponent, { ImageSchema } from '../Image/Image';
import { GalleryNavigationToolbar } from './GalleryNavigationToolbar';
import styles from './ImageGallery.module.css';
import { ImageZoomSlider } from './ImageZoomSlider';
import { useZoom } from './useZoom';

const maxScale = 143;
const minScale = 80;
const scaleStep = 10;

export interface GalleryImage extends ImageSchema {
  label?: string;
}

interface ImageGalleryProps<T extends GalleryImage> {
  images: T[];
  startImageIndex?: number;
  onClose: VoidFunction;
  disableZoom?: boolean;
  onImageVisible?: (image: T) => void;
}

export const ImageGallery = <T extends GalleryImage>({
  images,
  onClose,
  disableZoom,
  onImageVisible,
  startImageIndex = 0,
}: ImageGalleryProps<T>) => {
  const [imageIndex, setImageIndex] = useState(startImageIndex);

  const currentImage = images[imageIndex];

  const { zoom, setZoom, previewRef, previewContentRef, handlers: zoomHandlers } = useZoom({ disableZoom });

  const getPreviewClickHandler = (index: number) => () => {
    setImageIndex(index);
  };

  const getImageVisibleHandler = (image: T) => () => {
    onImageVisible?.(image);
  };

  const handleContainerClick = (event: React.MouseEvent) => {
    if (event.target === previewRef.current || event.target === previewContentRef.current) {
      onClose();
    }
  };

  const preventImageClick = (event: React.SyntheticEvent) => {
    event.stopPropagation();
  };

  const hasMultipleImages = images.length > 1;

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
    <div
      className={cn(styles.preview, { [c(styles.withoutZoom)]: disableZoom })}
      ref={previewRef}
      onClick={handleContainerClick}
    >
      <div
        ref={previewContentRef}
        data-qa-type="gallery-current-image"
        className={styles.previewContent}
        {...zoomHandlers}
      >
        {currentImage && (
          <ImageComponent
            className={styles.image}
            image={currentImage}
            onClick={preventImageClick}
            onImageVisible={getImageVisibleHandler(currentImage)}
            draggable={false}
          />
        )}
      </div>

      {!!currentImage?.label && <span className={styles.label}>{currentImage.label}</span>}

      {hasMultipleImages && (
        <GalleryNavigationToolbar
          className={styles.toolbar}
          imageLabel="Test"
          currentIndex={imageIndex}
          total={images.length}
          onChangeIndex={setImageIndex}
        />
      )}

      {!disableZoom && (
        <div className={styles.slider}>
          <ImageZoomSlider value={zoom} onChange={setZoom} min={minScale} max={maxScale} step={scaleStep} />
        </div>
      )}

      {hasMultipleImages && (
        <div className={styles.previewList}>
          {images.map((image, index) => (
            <ImageComponent
              key={image.src}
              className={cn(styles.imagePreview, { [c(styles.selected)]: index === imageIndex })}
              image={image}
              onClick={getPreviewClickHandler(index)}
              onImageVisible={getImageVisibleHandler(image)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
